import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/index.js';
import PageHeader from '../../components/PageHeader.jsx';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import { Chips, Group, Stepper, Toggle } from '../../components/Field.jsx';
import { saveCustomSession } from './customSession.js';
import { STUDY_MODES, planStudy, rootDecks, sessionTitle } from './studyPlan.js';
import styles from './StudySetup.module.scss';

const SECONDS_PER_CARD = 8;

function partsOf(counts) {
  const parts = [];
  if (counts.review) parts.push(`${counts.review} to review`);
  if (counts.learn) parts.push(`${counts.learn} in progress`);
  if (counts.new) parts.push(`${counts.new} new`);
  return parts;
}

export default function StudySetup() {
  const navigate = useNavigate();
  const data = useLiveQuery(async () => {
    const [decks, cards, notes] = await Promise.all([db.decks.toArray(), db.cards.toArray(), db.notes.toArray()]);
    return { decks, cards, notes };
  }, []);

  const [mode, setMode] = useState('today');
  const [deckIds, setDeckIds] = useState([]);
  const [days, setDays] = useState(7);
  const [limit, setLimit] = useState(20);
  const [tag, setTag] = useState('');
  const [reschedule, setReschedule] = useState(true);
  const [seed] = useState(() => String(Date.now()));

  const tags = useMemo(() => {
    const set = new Set();
    for (const note of data?.notes ?? []) {
      if (note.deletedAt) continue;
      for (const t of note.tags ?? []) set.add(t);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [data]);

  const activeTag = tag || tags[0] || '';

  const roots = data ? rootDecks(data.decks) : [];
  const modeDef = STUDY_MODES.find((m) => m.id === mode) ?? STUDY_MODES[0];
  const plan = data
    ? planStudy({
        cards: data.cards,
        notes: data.notes,
        decks: data.decks,
        mode,
        deckIds,
        days,
        tag: activeTag,
        limit,
        seed,
      })
    : null;

  const chooseMode = (id) => {
    const next = STUDY_MODES.find((m) => m.id === id);
    setMode(id);
    setReschedule(!!next?.reschedule);
    if (id === 'forgotten') setDays(7);
    if (id === 'ahead') setDays(3);
  };

  const deckValue = deckIds.length ? deckIds : ['all'];
  const onDecks = (next) => {
    const hadAll = deckValue.includes('all');
    const picked = next.filter((id) => id !== 'all');
    if (next.includes('all') && !hadAll) setDeckIds([]);
    else if (!picked.length || picked.length === roots.length) setDeckIds([]);
    else setDeckIds(picked);
  };

  const start = () => {
    if (!plan || !plan.total) return;
    if (plan.live) {
      const q = deckIds.length ? `?go=today&decks=${deckIds.join(',')}` : '?go=today';
      navigate(`/study${q}`);
      return;
    }
    if (mode === 'tag' && !activeTag) return;
    const id = saveCustomSession({
      title: sessionTitle(mode, deckIds, data.decks, activeTag),
      cardIds: plan.cardIds,
      reschedule,
      returnTo: '/study',
    });
    navigate(`/study?custom=${id}`);
  };

  if (!data || !plan) {
    return <div className={styles.page} aria-busy="true" />;
  }

  if (!roots.length) {
    return (
      <div className={styles.page}>
        <PageHeader title="Study" subtitle="Set up a short session." />
        <div className={styles.body}>
          <section className={styles.hero}>
            <h2>Make a deck first</h2>
            <p>Study needs a few cards to work with. A deck is just a stack on one topic.</p>
            <Button variant="primary" size="lg" icon="plus" to="/?newDeck=1">
              Create a deck
            </Button>
          </section>
        </div>
      </div>
    );
  }

  const minutes = Math.max(1, Math.round((plan.total * SECONDS_PER_CARD) / 60));
  const where =
    deckIds.length === 0
      ? roots.length === 1
        ? roots[0].name
        : 'All decks'
      : deckIds.length === 1
        ? roots.find((d) => d.id === deckIds[0])?.name
        : `${deckIds.length} decks`;

  let detail = modeDef.blurb;
  if (!plan.scopeCount) detail = roots.length === 1 ? `${roots[0].name} doesn’t have any cards yet.` : 'These decks don’t have any cards yet.';
  else if (!plan.total && mode === 'today') detail = 'Nothing is due. You’re caught up — try Mistakes, Ahead, or Mix.';
  else if (!plan.total) detail = 'Nothing matches. Try another option, or widen the decks and the time range.';
  else if (mode === 'today') detail = partsOf(plan.counts).join(' · ') || modeDef.blurb;
  else if (plan.available > plan.total) detail = `${plan.total} of ${plan.available} cards · ${modeDef.blurb}`;

  const caption = !plan.total
    ? null
    : mode === 'today'
      ? 'Answers update your schedule.'
      : reschedule
        ? 'Answers update when these cards come back.'
        : 'Practice only. Your schedule stays as it is.';

  return (
    <div className={styles.page}>
      <PageHeader title="Study" subtitle="Choose what you want to practice." />
      <div className={styles.body}>
        <Chips
          label="What to study"
          scroll
          value={mode}
          onChange={chooseMode}
          options={STUDY_MODES.map((m) => ({ value: m.id, label: m.label, icon: m.icon }))}
        />

        <section className={styles.hero} aria-live="polite">
          <div className={styles.heroTop}>
            <p className={styles.count}>
              {plan.total}
              <span>{plan.total === 1 ? 'card' : 'cards'}</span>
            </p>
            {plan.total ? (
              <p className={styles.time}>
                <Icon name="calendar" size={16} />
                About {minutes} min
              </p>
            ) : null}
          </div>
          <p className={styles.detail}>{detail}</p>
          <p className={styles.where}>{where}</p>
        </section>

        {roots.length > 1 ? (
          <Chips
            label="Decks"
            scroll
            multiple
            value={deckValue}
            onChange={onDecks}
            options={[{ value: 'all', label: 'All decks' }, ...roots.map((d) => ({ value: d.id, label: d.name }))]}
          />
        ) : null}

        {mode === 'tag' ? (
          tags.length ? (
            <Chips label="Tag" scroll value={activeTag} onChange={setTag} options={tags.map((t) => ({ value: t, label: t }))} />
          ) : (
            <p className={styles.note}>You haven’t tagged any cards yet. Add tags while editing a card.</p>
          )
        ) : null}

        {mode !== 'today' ? (
          <Group footer="A smaller number keeps the session short.">
            {modeDef.days ? (
              <Stepper
                label={modeDef.days}
                value={days}
                onChange={setDays}
                min={1}
                max={365}
                suffix={Number(days) === 1 ? 'day' : 'days'}
              />
            ) : null}
            <Stepper label="Up to" value={limit} onChange={setLimit} min={5} max={200} step={5} suffix="cards" />
            <Toggle
              label="Count toward my schedule"
              hint={reschedule ? 'Your answers change when cards come back.' : 'Just practice. Your schedule stays the same.'}
              checked={reschedule}
              onChange={setReschedule}
            />
          </Group>
        ) : (
          <p className={styles.note}>Today follows each deck’s daily limit, including cards inside subdecks.</p>
        )}
      </div>

      <div className={styles.dock}>
        {!plan.scopeCount ? (
          <Button variant="primary" size="lg" block icon="plus" to="/add">
            Add a card
          </Button>
        ) : (
          <Button variant="primary" size="lg" block icon="play" onClick={start} disabled={!plan.total || (mode === 'tag' && !activeTag)}>
            {plan.total ? (mode === 'today' ? 'Start today’s cards' : `Start · ${plan.total} ${plan.total === 1 ? 'card' : 'cards'}`) : 'Nothing to study'}
          </Button>
        )}
        {caption ? <p className={styles.caption}>{caption}</p> : null}
      </div>
    </div>
  );
}
